import type { PropsWithChildren } from "react"
import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { Link } from "@tanstack/react-router"

import { LucideIcon, Popover, Tooltip } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { HelpArticleSuggestionItem } from "./build-items"
import type { HelpArticlesProps } from "./help-article-suggestions"
import type { SuggestionProps } from "@tiptap/suggestion"

export const HelpArticlesPopover = forwardRef<
	{ setOpen: (open: boolean) => void },
	PropsWithChildren<
		{
			items: HelpArticleSuggestionItem[]
			onSelect: (index: number) => void
			initialOpen?: boolean
			anchorRef?: React.RefObject<HTMLElement | null>
		} & Pick<SuggestionProps, "editor"> &
			HelpArticlesProps
	>
>(
	(
		{
			width,
			children,
			withoutPortal,
			items,
			onSelect,
			editor,
			initialOpen = true,
			anchorRef,
			caretRef,
		},
		ref,
	) => {
		const [isPopoverOpen, setPopoverOpen] = useState(initialOpen)

		useImperativeHandle(ref, () => ({
			setOpen: setPopoverOpen,
		}))

		useEffect(() => {
			const onKeydown = (event: KeyboardEvent) => {
				if (event.key === "Escape") {
					editor.commands.focus()
				}
			}

			if (!isPopoverOpen) return

			document.addEventListener("keydown", onKeydown)
			return () => document.removeEventListener("keydown", onKeydown)
		}, [isPopoverOpen, editor])

		return (
			<Popover
				{...(anchorRef && caretRef?.current
					? {
							anchor: (
								<div
									className="pointer-events-none absolute h-[1.2em] w-px"
									style={{
										top: caretRef.current.top,
										left: caretRef.current.left,
									}}
								/>
							),
							anchorContainerRef: anchorRef,
						}
					: anchorRef
						? {
								anchor: (
									<div className="pointer-events-none absolute inset-x-0 top-0 h-px" />
								),
								anchorContainerRef: anchorRef,
							}
						: withoutPortal
							? {}
							: { anchor: <div className="h-4" /> })}
				side={
					anchorRef && caretRef?.current
						? "bottom"
						: anchorRef
							? "top"
							: undefined
				}
				align={
					anchorRef && caretRef?.current
						? "start"
						: anchorRef
							? "end"
							: undefined
				}
				sideOffset={anchorRef ? 8 : undefined}
				onOpenChange={setPopoverOpen}
				open={isPopoverOpen}
				modal={false}
				style={{ width: width ? `${width}px` : undefined }}
				className={`min-w-full p-1 [&_[cmdk-input-wrapper]]:px-3`}
				onOpenAutoFocus={(e) => {
					e.preventDefault()
				}}
				content={
					items.length === 0 ? (
						<div className="text-sm">
							No help center articles found. Create one{" "}
							<Link to="/edit-docs" className="underline">
								here
							</Link>
						</div>
					) : (
						<Command
							loop
							filter={(_, search, keywords) =>
								keywords?.find((keyword) =>
									keyword.toLowerCase().includes(search),
								)
									? 1
									: 0
							}
						>
							<CommandInput autoFocus placeholder="Choose help article…" />
							<CommandList className="max-h-80">
								<CommandEmpty>No help articles found</CommandEmpty>
								<CommandGroup>
									{items.map(({ title, url, id, icon }, index) => (
										<CommandItem
											keywords={[title, url]}
											key={`help-article-suggestion-${id}`}
											value={id}
											size="lg"
											className="px-2"
											onSelect={() => {
												onSelect(index)
											}}
										>
											<div className="flex flex-1 items-center gap-3 overflow-hidden">
												<LucideIcon
													icon={icon}
													className="h-4 w-4 flex-none text-[var(--portal-accent-color)]"
												/>
												<div className="flex min-w-0 flex-1 items-baseline gap-3 overflow-hidden whitespace-nowrap">
													<div className="flex-none font-medium text-primary">
														{title}
													</div>
													<div className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-normal text-tertiary">
														{url}
													</div>
												</div>
											</div>
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>
						</Command>
					)
				}
			>
				{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
				<div onClick={() => setPopoverOpen(!isPopoverOpen)}>
					<Tooltip label="Choose help article">{children}</Tooltip>
				</div>
			</Popover>
		)
	},
)
